import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Building2, Users, Gift, QrCode, CheckCircle2, Rocket, TrendingUp, MapPin, Bell, MessageSquare, ArrowRight, Shield } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { FreeOrgBadge, FREE_ORG_TEXT } from "@/components/FreeOrgBadge";
import { SpaceAccessBar } from "@/components/SpaceAccessBar";

const PROFILES = [
  { k: "cse", label: "CSE / CSE" , desc: "Comité social et économique d'entreprise." },
  { k: "entreprise", label: "Entreprise", desc: "Offrir des avantages à vos salariés." },
  { k: "association", label: "Association", desc: "Enrichir la vie de vos adhérents." },
  { k: "club", label: "Club sportif / culturel", desc: "Fidéliser vos membres." },
  { k: "amicale", label: "Amicale", desc: "Bonifier l'appartenance à votre amicale." },
  { k: "administration", label: "Administration", desc: "Avantages pour vos agents." },
  { k: "collectivite", label: "Collectivité", desc: "Politique locale d'accompagnement." },
  { k: "autre", label: "Autre organisme", desc: "Coopérative, syndicat, autre structure." },
];

export default function OrganismePresentation() {
  useSeo({
    title: "Organismes & collectivités — avantages locaux pour vos membres | PROXIreduc-28",
    description: "Collectivités, CSE, mutuelles et organismes : offrez des avantages locaux à vos membres en Eure-et-Loir avec PROXIreduc-28. Visibilité, bons plans et outils numériques gratuits.",
    canonicalPath: "/organismes-collectivites",
    image: "/og-image.jpg",
  });
  return (
    <div className="bg-[#FAF8F5]" data-testid="org-presentation-page">
      <SpaceAccessBar to="/organismes-collectivites/inscription" label="Créer mon espace organisme — gratuit"
        note="Collectivité, amicale, administration ?" testId="org-access-bar"
        spaceTo="/organisme" spaceLabel="Accéder à mon espace organisme" />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-white to-orange-50 pointer-events-none"/>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 md:pt-20 pb-10 md:pb-14">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-emerald-200 text-emerald-800 text-xs font-bold uppercase tracking-widest">
            <Building2 size={14}/> Organismes & Collectivités
          </span>
          <FreeOrgBadge className="mt-4 ml-0 sm:ml-2" />
          <p className="mt-3 font-heading text-base md:text-lg font-extrabold text-emerald-800" data-testid="org-presentation-free-line">
            {FREE_ORG_TEXT}
          </p>
          <h1 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-6xl font-extrabold text-slate-900 leading-tight">
            Vos <span className="text-[#E05638]">avantages locaux</span>, réunis au même endroit
          </h1>
          <p className="mt-4 max-w-3xl text-base md:text-lg text-slate-700">
            PROXIreduc-28 permet aux CSE, entreprises, associations, clubs, amicales, administrations et collectivités
            de proposer à leurs salariés, adhérents, membres ou agents une sélection d'offres et d'avantages
            auprès de professionnels locaux partenaires en Eure-et-Loir.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/organismes-collectivites/inscription"><Button className="btn-shine text-white h-12 px-6" data-testid="org-cta-register"><Rocket size={16} className="mr-2"/> Créer mon espace</Button></Link>
            <a href="#fonctionnement"><Button variant="outline" className="h-12 px-6" data-testid="org-cta-discover">Découvrir le fonctionnement</Button></a>
          </div>
        </div>
      </section>

      {/* 4 avantages */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { i: MapPin,   t: "Des offres locales",             d: "Sélection de commerces et pros du 28." },
            { i: Users,    t: "Des offres réservées",           d: "Avantages exclusifs à votre organisme." },
            { i: Shield,   t: "Un espace privé pour vos membres", d: "Chaque organisme a sa base séparée et sécurisée." },
            { i: QrCode,   t: "Un Pass numérique avec QR code", d: "Vérification instantanée en boutique." },
          ].map((a, i)=>(
            <div key={i} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`org-benefit-${i}`}>
              <div className="w-11 h-11 rounded-xl bg-orange-50 text-[#E05638] grid place-items-center"><a.i size={20}/></div>
              <div className="mt-3 font-heading font-extrabold text-slate-900">{a.t}</div>
              <p className="mt-1 text-sm text-slate-600">{a.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Profils */}
      <section id="fonctionnement" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-xs font-bold uppercase tracking-widest text-emerald-700">À qui s'adresse PROXIreduc-28 ?</div>
        <h2 className="font-heading text-3xl md:text-4xl font-extrabold mt-2">8 profils d'organismes</h2>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          {PROFILES.map((p)=>(
            <div key={p.k} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`org-profile-${p.k}`}>
              <div className="font-heading font-bold text-slate-900">{p.label}</div>
              <p className="mt-1 text-xs text-slate-500">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pourquoi */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="font-heading text-3xl md:text-4xl font-extrabold">Pourquoi rejoindre PROXIreduc-28 ?</h2>
        <div className="mt-6 grid md:grid-cols-2 gap-3">
          {[
            { i: TrendingUp,    t: "Valoriser les commerces locaux",  d: "Soutenez l'économie de proximité." },
            { i: Gift,          t: "Avantages supplémentaires",       d: "Étoffez ce que vous offrez à vos membres." },
            { i: Building2,     t: "Centraliser les offres",          d: "Fini les tableaux Excel dispersés." },
            { i: CheckCircle2,  t: "Faciliter l'accès",               d: "Un espace unique, mobile-first." },
            { i: TrendingUp,    t: "Suivre l'utilisation",            d: "Stats claires : consultations, clics, utilisations." },
            { i: MessageSquare, t: "Communiquer facilement",          d: "Notifications, mises en avant, newsletters." },
          ].map((f, i)=>(
            <div key={i} className="bg-white border border-slate-200 rounded-2xl p-5 flex items-start gap-3" data-testid={`org-why-${i}`}>
              <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-700 grid place-items-center shrink-0"><f.i size={20}/></div>
              <div>
                <div className="font-heading font-bold text-slate-900">{f.t}</div>
                <p className="text-sm text-slate-600 mt-0.5">{f.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA final */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 text-white p-8 md:p-12 grid md:grid-cols-2 gap-6 items-center">
          <div>
            <h3 className="font-heading text-2xl md:text-3xl font-extrabold">Prêt à faire de vos avantages un vrai différenciant ?</h3>
            <p className="mt-3 text-white/80">Créez votre espace en 5 minutes. Notre équipe le valide sous 24-48 h.</p>
            <p className="mt-3 font-semibold text-emerald-300" data-testid="org-cta-free-line">{FREE_ORG_TEXT}</p>
          </div>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <Link to="/organismes-collectivites/inscription"><Button className="btn-shine text-white h-12 px-6" data-testid="org-cta-register-2">Créer mon espace <ArrowRight size={14} className="ml-2"/></Button></Link>
            <Link to="/contact"><Button variant="outline" className="text-slate-900 h-12 px-6">Nous contacter</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
